import {BookOfShadowsReelLockView} from "./BookOfShadowsReelLockView";
import {EventHandler} from "@nolimitcity/slot-game/bin/core/event/EventHandler";
import {GameEvent} from "@nolimitcity/slot-game/bin/core/event/GameEvent";
import {BalanceEvent} from "@nolimitcity/slot-game/bin/core/balance/event/BalanceEvent";
import {ServerEvent} from "@nolimitcity/slot-game/bin/core/server/event/ServerEvent";
import {BookOfShadowsParsedInitData} from "../server/data/BookOfShadowsParsedInitData";
import {GameSetting} from "@nolimitcity/slot-game/bin/core/setting/GameSetting";
import {IBookOfShadowsReplayNextPlayerInteraction} from "../transitions/freeSpinIntro/BookOfShadowsFreeSpinIntroView";
import {BookOfShadowsParsedGameData} from "../server/data/BookOfShadowsParsedGameData";
import {ILockedReelsDetails} from "../server/data/IBookOfShadowsServerData";
import {BookOfShadowsGameMode} from "../gameMode/BookOfShadowsGameMode";
import {APIBetType} from "@nolimitcity/slot-launcher/bin/interfaces/APIBetType";
import {IFeatureBetEventData} from "../inactiveRow/BookOfShadowsInactiveRowController";
import {BookOfShadowsGameConfig} from "../BookOfShadowsGameConfig";
import {BookOfShadows} from "../BookOfShadows";

export interface ILockedReelDetailItems {
    [name: string]: ILockedReelsDetails;
}

export class BookOfShadowsReelLockController {
    private _view: BookOfShadowsReelLockView;
    private _mode: string = "";

    /**
     * @constructs
     */
    constructor() {
        this._view = new BookOfShadowsReelLockView();

        this.addEventListeners();
    }

    private _lockedReelDetails: ILockedReelDetailItems = {};

    get lockedReelDetails(): ILockedReelDetailItems {
        return this._lockedReelDetails;
    }

    /**
     * @description To trigger the lock idle animation.
     * @param id
     */
    public playLockIdleAnimation(id: number): void {
        this._view.playLockIdleAnimation(id);
    }

    /**
     * @description When entered in FS.
     */
    public enteredFS(): void {
        this._view.stopReelLockSpineAnimations(false);
    }

    /**
     * @description When exited from FS.
     */
    public exitedFS(): void {
        this._view.stopReelLockSpineAnimations(true);
    }

    /**
     * @description To toggle view's lock button from any where in the game.
     * @param val
     */
    public toggleLockButtonsEnable(val: boolean): void {
        this._view.toggleButtonEnable(val);
    }

    /**
     * @description To play bounce animation.
     */
    public playBounceForGameTutorial(): void {
        this._view.playBounceForGameTutorial();
    }

    /**
     * @description To stop bounce animation.
     */
    public stopBounceForGameTutorial(): void {
        this._view.stopBounceForGameTutorial();
    }

    /**
     * @description To call view's displayLockCosts method.
     * @param calledFromSpinStart
     */
    public displayReelLockCosts(calledFromSpinStart?: boolean): void {
        if (!BookOfShadowsGameConfig.instance.LUCKY_LOCKS_ALLOWED_VIA_BET) {
            return;
        }
        if (this._mode !== BookOfShadowsGameMode.FREESPIN) {
            this._view.displayLockCosts(calledFromSpinStart);
        }
    }

    /**
     * @description To act as finish.
     */
    public actFinish(): void {
        if (!BookOfShadowsGameConfig.instance.LUCKY_LOCKS_ALLOWED_VIA_BET) {
            this.exitedFS();
            BookOfShadows.setBoostedBet();
            // #232
            this._view.betPlaced = false;
            return;
        }

        this._view.onRoundFinished();
        this.displayReelLockCosts(false);
    }

    /**
     * @description To register events.
     */
    private addEventListeners(): void {
        EventHandler.addEventListener(this, ServerEvent.INIT_DATA_PARSED, (event: GameEvent) => this.onInitDataParsed(event.params[0]));
        EventHandler.addEventListener(this, ServerEvent.GAME_DATA_PARSED, (event: GameEvent) => this.onGameDataParsed(event.params[0]));
        EventHandler.addEventListener(this, BalanceEvent.BET, (event: GameEvent) => this.onBet(event.params[0]));
    }

    /**
     * @description Method called when init data is parsed.
     * @param data
     */
    private onInitDataParsed(data: BookOfShadowsParsedInitData): void {
        let replayNextPlayerInteraction: IBookOfShadowsReplayNextPlayerInteraction;

        if (data.isRestoreState && data.nextReelSetName === "FREESPIN_REELSET" && !data.wasFeatureBuy && data.lockedReelBet > 0) {
            const lockedReelDetails: any = [];
            lockedReelDetails.push({
                numberRows: data.selectedRows,
                price: data.lockedReelBet,
                setup: "1,0,0,0,0" // this is only used for setting boosted bet in FS restore only
            });
            BookOfShadowsGameConfig.instance.LOCKED_REEL = ["1", "0", "0", "0", "0"]; // this is only used for setting boosted bet in FS restore only, it will be reset once FS is over.

            this.setLockedReelDetails(lockedReelDetails);
        } else {
            this.setLockedReelDetails(data.lockedReelDetails);
        }

        if (data.lockedReelsSet) {
            BookOfShadowsGameConfig.instance.LOCKED_REEL = data.lockedReelsSet.split(",");
        }

        if (GameSetting.replayMode) {
            replayNextPlayerInteraction = (data.replayNextPlayerInteraction as IBookOfShadowsReplayNextPlayerInteraction);
            if (replayNextPlayerInteraction.lockedReel) {
                if (replayNextPlayerInteraction.baseRows && replayNextPlayerInteraction.price) {
                    const lockedReelDetails: any = [];
                    lockedReelDetails.push({
                        numberRows: parseInt(replayNextPlayerInteraction.baseRows),
                        price: replayNextPlayerInteraction.price,
                        setup: replayNextPlayerInteraction.lockedReel
                    });

                    this.setLockedReelDetails(lockedReelDetails);
                }
                BookOfShadowsGameConfig.instance.LOCKED_REEL = replayNextPlayerInteraction.lockedReel.split(",");
                this._view.handleReplay();
            }
        }

        this._mode = data.mode;

        this._view.onGameInit(data.isRestoreState, data.nextReelSetName);

        // #179
        if ((data.isRestoreState && !data.wasFeatureBuy) || GameSetting.replayMode) {
            BookOfShadows.setBoostedBet();
        }

        EventHandler.removeEventListener(this, ServerEvent.INIT_DATA_PARSED);
    }

    private onGameDataParsed(data: BookOfShadowsParsedGameData) {
        this._mode = data.mode;

        const currentBetLevel: string = BookOfShadows.api.betLevel.getLevel(),
            currentBetLevelFloat = parseFloat(currentBetLevel);

        this.setLockedReelDetails(data.lockedReelDetails);

        // #214
        if (currentBetLevelFloat === data.playedBetValue) {
            this._view.onGameData(data.allowLockedReelsNextSpin);
        } else {
            this._view.onGameData(false);
            BookOfShadowsGameConfig.instance.LUCKY_LOCKS_ALLOWED_VIA_BET = false;
        }
    }

    private setLockedReelDetails(lockedReelDetails: ILockedReelsDetails[]): void {
        let detail: ILockedReelsDetails;

        this._lockedReelDetails = {};
        for (let j: number = 0; j < lockedReelDetails.length; j++) {
            detail = lockedReelDetails[j];

            this.lockedReelDetails[detail.numberRows + "_" + detail.setup] = lockedReelDetails[j];
        }
    }

    /**
     * @description When we hit a bet request, only interested in FREESPIN feature buy.
     * @param bet
     */
    private onBet(bet: IFeatureBetEventData): void {
        const featureName: string | undefined = bet.featureName;

        if (bet.type === APIBetType.FEATURE_BET && featureName && featureName.indexOf("FREESPIN") > -1) {
            this._view.stopReelLockSpineAnimations(false);
        }

        this._view.onBet();
    }
}